/**
 * Reusable hook for managing library storage connections.
 *
 * Encapsulates backend selection (FSAA / Google Drive / OPFS), OAuth redirect
 * handling, session restoration, and cache delegation. Consumers get
 * multi-backend support with a single hook call.
 *
 * Connection state is stored in a global Zustand store so it persists
 * across page navigation.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useLibraryConnectionStore, connectToBackend, disconnectFromBackend, clearConnectionCache, getConnectionMetrics, resetConnectionMetrics, handleGoogleDriveRedirect, getSavedBackendPreference, } from '../stores/libraryConnectionStore';
// =========================================================================
// Hook
// =========================================================================
export function useLibraryConnection(config = {}) {
    // Get state from global store
    const activeBackend = useLibraryConnectionStore((s) => s.activeBackend);
    const root = useLibraryConnectionStore((s) => s.root);
    const initRef = useRef(false);
    const hasLocalFS = 'showDirectoryPicker' in globalThis;
    const hasGoogleDrive = config.googleDrive != null;
    const hasOPFS = 'storage' in navigator;
    // Build config object for store functions
    const storeConfig = {
        pickerId: config.pickerId,
        googleDrive: config.googleDrive,
    };
    // Connect to a backend
    const connect = useCallback(async (backend) => {
        return connectToBackend(backend, storeConfig);
    }, [storeConfig.pickerId, storeConfig.googleDrive?.clientId, storeConfig.googleDrive?.clientSecret]);
    // Disconnect
    const disconnect = useCallback(() => {
        disconnectFromBackend();
    }, []);
    // Cache delegation
    const clearCache = useCallback(() => {
        clearConnectionCache();
    }, []);
    const getMetrics = useCallback(() => {
        return getConnectionMetrics();
    }, []);
    const resetMetrics = useCallback(() => {
        resetConnectionMetrics();
    }, []);
    // On mount: try to restore the previously active backend
    useEffect(() => {
        if (initRef.current)
            return;
        initRef.current = true;
        if (config.googleDrive) {
            void handleGoogleDriveRedirect(storeConfig);
        }
        // If already connected (e.g., navigating between pages), nothing to do
        if (activeBackend !== 'none')
            return;
        const savedBackend = getSavedBackendPreference();
        if (savedBackend === 'none')
            return;
        // Auto-reconnect to the previously active backend.
        // OPFS and FSAA can both restore without user interaction.
        void connectToBackend(savedBackend, storeConfig).then((restored) => {
            if (!restored) {
                console.warn(`[useLibraryConnection] Failed to restore ${savedBackend} backend`);
            }
        });
    }, [config.googleDrive, storeConfig, activeBackend]);
    return {
        activeBackend,
        isConnected: root !== null,
        root,
        connect,
        disconnect,
        clearCache,
        getMetrics,
        resetMetrics,
        hasLocalFS,
        hasGoogleDrive,
        hasOPFS,
    };
}
//# sourceMappingURL=useLibraryConnection.js.map